/**
 * Weapons.
 *
 * A weapon is **data**: a name, what it does, how far it reaches, the move it
 * grants, and a count of how many swings are left in it. Everything else in
 * this module is the consequence of three rules, and the three rules are what
 * make a weapon a different thing from a very good punch:
 *
 *   **Unblockable.** A guard is worth nothing against a pipe. This is the whole
 *   reason to want one, and the whole reason to fear one.
 *   **Cannot be comboed.** Not into, and not out of. A weapon swing is a single
 *   committed act — it ends whatever chain was running and starts none. Without
 *   this, an unblockable opener with a cancel window is not a weapon, it is a
 *   win button.
 *   **Can be reversed.** Catch somebody mid-swing and the weapon is **yours**.
 *   That is the counterplay, and it is the reason the first two are allowed to
 *   be as strong as they are. It reuses the counter the game has had since step
 *   12: a blow that lands on a fighter committed to something else.
 *
 * The move a weapon grants is *derived* from the weapon rather than declared
 * twice. `weaponMoves()` assembles a move definition the strike layer already
 * understands, so a weapon needs no entry in `moves.json` and there is one
 * place to edit when a bat should hit harder.
 *
 * Pure. No three.js.
 */

import WEAPONS from './weapons.json';
import { STRENGTHS } from './strikeLayer.js';
import { clampToBounds } from './locomotion.js';

export { WEAPONS };

export class WeaponConfigError extends Error {}

/** The tag every derived weapon move carries, for anything that has to ask. */
export const WEAPON_TAG = 'weapon';

export function weaponIds(config = WEAPONS) {
  return Object.keys(config.weapons ?? {});
}

export function validateWeapons(config = WEAPONS) {
  const weapons = config?.weapons;
  if (!weapons || typeof weapons !== 'object') {
    throw new WeaponConfigError('weapon set has no "weapons" object');
  }
  if (Object.keys(weapons).length === 0) {
    throw new WeaponConfigError('weapon set is empty');
  }

  const moveNames = new Set();
  for (const [id, weapon] of Object.entries(weapons)) {
    if (!weapon.name) throw new WeaponConfigError(`weapon "${id}" has no display name`);
    if (!weapon.move) throw new WeaponConfigError(`weapon "${id}" grants no move`);
    if (moveNames.has(weapon.move)) {
      // Two weapons granting one move would share a hitbox, a clip and a set of
      // frames, and only one of them could be the source of truth for it.
      throw new WeaponConfigError(`two weapons grant the move "${weapon.move}"`);
    }
    moveNames.add(weapon.move);

    if (!(weapon.damage > 0)) {
      throw new WeaponConfigError(`weapon "${id}" must do positive damage`);
    }
    if (!STRENGTHS.includes(weapon.strength)) {
      throw new WeaponConfigError(
        `weapon "${id}" has strength "${weapon.strength}"; expected ${STRENGTHS.join(' or ')}`
      );
    }
    if (!(weapon.reach > 0)) {
      throw new WeaponConfigError(`weapon "${id}" must declare a positive reach`);
    }
    // The count is the whole of a weapon's economy. One that never runs out is
    // not a pickup, it is a permanent upgrade.
    if (!(Number.isInteger(weapon.durability) && weapon.durability > 0)) {
      throw new WeaponConfigError(
        `weapon "${id}" has a durability of ${weapon.durability}; want a whole number of swings`
      );
    }
    if (!weapon.clip) throw new WeaponConfigError(`weapon "${id}" has no clip to swing with`);
    if (!weapon.hitbox) throw new WeaponConfigError(`weapon "${id}" has no hitbox`);
    for (const phase of ['startup', 'active', 'recovery']) {
      if (!Number.isInteger(weapon[phase]) || weapon[phase] < 0) {
        throw new WeaponConfigError(
          `weapon "${id}" has ${phase} of ${weapon[phase]}; want whole frames`
        );
      }
    }
    if (!(weapon.active > 0)) {
      throw new WeaponConfigError(`weapon "${id}" has no active frames`);
    }
  }

  const pickup = config.pickup ?? {};
  if (!(pickup.radius > 0)) {
    throw new WeaponConfigError('a dropped weapon needs a positive pickup radius');
  }
  if (!(pickup.life > 0)) {
    throw new WeaponConfigError('a dropped weapon needs a positive life');
  }
  return config;
}

/**
 * The move each weapon grants, as move definitions the strike layer validates
 * like any other.
 *
 * Derived rather than declared, so `weapons.json` is the only place a bat's
 * damage lives. Three fields are the rules of the thing and are *not*
 * per-weapon: every weapon move is unblockable, is not comboable either way,
 * and can be reversed.
 *
 * Note what is missing: no `follows`, and no `cancel`. A move that declares
 * neither cannot be chained into, and `comboable: false` is what stops it being
 * chained *out of* — `combo.js` would otherwise hand it the default window.
 */
export function weaponMoves(config = WEAPONS) {
  validateWeapons(config);
  const moves = {};
  for (const [id, weapon] of Object.entries(config.weapons)) {
    moves[weapon.move] = Object.freeze({
      name: weapon.name,
      kind: 'strike',
      weapon: id,
      clip: weapon.clip,
      state: 'strike',
      hitbox: weapon.hitbox,
      startup: weapon.startup,
      active: weapon.active,
      recovery: weapon.recovery,
      damage: weapon.damage,
      strength: weapon.strength,
      reach: weapon.reach,
      knockback: weapon.knockback ?? 0.2,
      unblockable: true,
      comboable: false,
      reversible: true,
      tags: Object.freeze([WEAPON_TAG]),
    });
  }
  return moves;
}

/** Is this move something somebody is holding? */
export const isWeaponMove = (move) => Boolean(move?.weapon);

/**
 * One weapon, in somebody's hands or lying on the floor.
 *
 * An *instance*, not a definition: it carries the swings it has left, and it is
 * the same object as it changes hands. A reversal moves this across rather than
 * making the reverser a new one, which is the difference between taking a
 * half-broken bottle off somebody and being handed a fresh one.
 */
export function createWeapon(id, { config = WEAPONS, durability } = {}) {
  const spec = config.weapons?.[id];
  if (!spec) throw new WeaponConfigError(`no such weapon "${id}"`);

  let left = durability ?? spec.durability;
  let holder = null;
  const listeners = new Set();

  return {
    id,
    spec,
    name: spec.name,
    move: spec.move,

    get durability() {
      return left;
    },
    get max() {
      return spec.durability;
    },
    get fraction() {
      return left / spec.durability;
    },
    get broken() {
      return left <= 0;
    },

    /** Whose hands it is in, or null while it is on the floor. */
    get holder() {
      return holder;
    },
    heldBy(who) {
      holder = who ?? null;
      return holder;
    },

    /**
     * Spends a swing. Returns what is left, or false if there was nothing to
     * spend — a broken weapon refuses rather than going negative.
     *
     * Charged on the **swing**, not on the hit. An unblockable move needs a
     * cost, and "you missed and it cost you anyway" is that cost; a weapon that
     * only wore out when it connected would make whiffing free.
     */
    swing() {
      if (left <= 0) return false;
      left -= 1;
      for (const listener of listeners) {
        listener({ weapon: this, left, broke: left <= 0, holder });
      }
      return left;
    },

    /** Fires on every swing, and carries `broke` on the one that ends it. */
    onSwing(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    reset() {
      left = spec.durability;
      holder = null;
    },
  };
}

/**
 * Whether this blow is a reversal, and of what.
 *
 * A reversal is a **counter landed on somebody mid-swing**. Not on somebody
 * merely holding a weapon — walking up and punching an armed fighter takes
 * nothing off them — and not any old hit: the counter is already the game's
 * word for "you caught them committed to something", and a weapon swing is the
 * most committed thing in it.
 *
 * Returns the weapon that changes hands, or null.
 */
export function reversalOf(defender, { counter = false } = {}) {
  if (!counter) return null;
  const weapon = defender?.weapon;
  if (!weapon || weapon.broken) return null;
  // Mid-swing, specifically. `strikes.strike` is the move currently out.
  const swinging = defender.strikes?.active ? defender.strikes.strike : null;
  if (!swinging || swinging !== weapon.move) return null;
  // And the move has to say it can be. Every weapon says so today, so this
  // changes nothing — but the flag was declared, documented as one of the three
  // rules a weapon has, and read by **nobody**, which made it a comment
  // wearing a field's clothes. The audit in step 43 found it.
  if (defender.strikes?.get?.(swinging)?.reversible === false) return null;
  return weapon;
}

/**
 * The floor: weapons lying on it, and the rule for getting one off it.
 *
 * The crowd does not put a bat *into* anybody's hand. It throws one down near
 * the fighter it likes, and **either of them can pick it up** — which is the
 * whole of what makes the crowd's favour an opportunity rather than a gift, and
 * the reason a cold fighter is not simply losing: the weapon the crowd threw for
 * the other man is lying on the floor between them.
 *
 * Pure. The picture is `weaponModel.js`; this is where one actually is.
 */
export function createWeaponGround({ config = WEAPONS, bounds = null } = {}) {
  validateWeapons(config);
  const pickup = config.pickup;
  let items = [];
  let floor = bounds;
  const dropListeners = new Set();
  const pickupListeners = new Set();

  return {
    config,
    /** What is lying about, oldest first. */
    get items() {
      return items;
    },
    get count() {
      return items.length;
    },

    /** The walkable floor, so nothing lands somewhere nobody can reach it. */
    setBounds(next) {
      floor = next ?? null;
      return floor;
    },

    /**
     * Puts a weapon on the floor at `at`, clamped onto the walkable floor.
     *
     * Clamped rather than refused: a weapon thrown two metres behind a fighter
     * standing against the back wall would land inside the tiling, and a pickup
     * nobody can reach is worse than one that landed a bit short.
     */
    drop(weapon, at, { forWhom = null, reason = null } = {}) {
      if (!weapon || weapon.broken) return null;
      weapon.heldBy(null);
      const spot = floor ? clampToBounds({ x: at.x, z: at.z }, floor) : { x: at.x, z: at.z };
      const item = {
        weapon,
        x: spot.x,
        z: spot.z,
        /** Who it was meant for. Nothing enforces it; the list just knows. */
        forWhom,
        reason,
        left: pickup.life,
      };
      items = [...items, item];
      for (const listener of dropListeners) listener({ ...item, id: weapon.id });
      return item;
    },

    /**
     * Ages what is lying about and hands anything within reach to whoever is
     * standing on it.
     *
     * A fighter already holding something walks straight over it: swapping
     * mid-fight for a worse weapon by accident is not a decision anybody made.
     */
    update(dt, fighters = []) {
      if (!(dt > 0)) return items;
      const taken = [];
      const kept = [];

      for (const item of items) {
        item.left -= dt;
        const finder = fighters.find(
          (fighter) =>
            fighter?.position &&
            !fighter.armed &&
            Math.hypot(fighter.position.x - item.x, fighter.position.z - item.z) <= pickup.radius
        );
        if (finder) {
          finder.arm(item.weapon);
          taken.push({ ...item, by: finder.id ?? null, id: item.weapon.id });
          continue;
        }
        if (item.left > 0) kept.push(item);
      }

      items = kept;
      for (const event of taken) {
        for (const listener of pickupListeners) listener(event);
      }
      return items;
    },

    onDrop(listener) {
      dropListeners.add(listener);
      return () => dropListeners.delete(listener);
    },
    onPickup(listener) {
      pickupListeners.add(listener);
      return () => pickupListeners.delete(listener);
    },

    clear() {
      items = [];
    },
  };
}

/**
 * Where a weapon thrown *for* somebody lands.
 *
 * Behind them, away from their opponent, at `distance`. Near enough to be
 * theirs for the taking and far enough that taking it costs a step — and with
 * their back turned to fetch it, which is the moment the other fighter has to
 * decide whether to contest it or hit them.
 */
export function dropSpot(forWhom, against, distance = 1.15) {
  const to = forWhom?.position;
  const from = against?.position;
  if (!to) return { x: 0, z: 0 };
  if (!from) return { x: to.x, z: to.z + distance };
  const dx = to.x - from.x;
  const dz = to.z - from.z;
  const length = Math.hypot(dx, dz);
  if (length < 1e-6) return { x: to.x, z: to.z + distance };
  return { x: to.x + (dx / length) * distance, z: to.z + (dz / length) * distance };
}
